import React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import SignInPage from "./pages/SignInPage";
import PortfolioPage from "./pages/PortfolioPage";
import TopicsPage from "./pages/TopicsPage";
import TopicPage from "./pages/TopicPage";
import LessonDetailPage from "./pages/LessonDetailPage";
import GraphPage from "./pages/GraphPage";
import GraphNodePage from "./pages/GraphNodePage";
import GraphOrderPage from "./pages/GraphOrderPage";
import SettingsPage from "./pages/SettingsPage";
import EnglishDashboardPage from "./pages/EnglishDashboardPage";
import EnglishLessonPage from "./pages/EnglishLessonPage";
import EnglishCoachPage from "./pages/EnglishCoachPage";
import EnglishPronunciationPage from "./pages/EnglishPronunciationPage";

// The Knowledge Base moved from / to /articles. These keep old bookmarks alive.
// A bare <Navigate to="/articles/topic/:topicSlug"> would not work — React
// Router does not interpolate params into the `to` string.
function LegacyTopicRedirect() {
  const { topicSlug } = useParams();
  return <Navigate to={`/articles/topic/${topicSlug}`} replace />;
}

function LegacyLessonRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/articles/lesson/${slug}`} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Landing page — the portfolio. Static, no API call. */}
          <Route path="/" element={<PortfolioPage />} />

          {/* Knowledge Base — content visibility is enforced by the backend. */}
          <Route path="/articles" element={<TopicsPage />} />
          <Route path="/articles/topic/:topicSlug" element={<TopicPage />} />
          <Route path="/articles/lesson/:slug" element={<LessonDetailPage />} />

          {/* Knowledge graph — concepts, and the reading order
              the dependency edges imply. Public, like /articles. */}
          {/* The knowledge graph is one person's: their nodes, their readings,
              their schedule. Every endpoint behind it requires a user, so the
              pages do too — an anonymous visitor would get 401s and a blank
              screen instead of a sign-in prompt. */}
          <Route
            path="/graph"
            element={
              <ProtectedRoute>
                <GraphPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/graph/node/:slug"
            element={
              <ProtectedRoute>
                <GraphNodePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/graph/order/:slug"
            element={
              <ProtectedRoute>
                <GraphOrderPage />
              </ProtectedRoute>
            }
          />

          <Route path="/signin" element={<SignInPage />} />

          {/* Admin only. */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />

          {/* Admin only — and the guard here is UX, not security: the coach API,
              its audio route and its voice socket all refuse anyone without an
              admin token. Dashboard -> lesson -> microphone, in that order. */}
          <Route
            path="/english"
            element={
              <ProtectedRoute>
                <EnglishDashboardPage />
              </ProtectedRoute>
            }
          />
          {/* Before /english/:slug for readability; Router ranks the static
              segment higher either way, so "pronunciation" is never a slug. */}
          <Route
            path="/english/pronunciation"
            element={
              <ProtectedRoute>
                <EnglishPronunciationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/english/:slug"
            element={
              <ProtectedRoute>
                <EnglishLessonPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/english/:slug/practice"
            element={
              <ProtectedRoute>
                <EnglishCoachPage />
              </ProtectedRoute>
            }
          />

          {/* Pre-/articles links. */}
          <Route path="/topic/:topicSlug" element={<LegacyTopicRedirect />} />
          <Route path="/lesson/:slug" element={<LegacyLessonRedirect />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
